/**
 * Этапы техкарты (ADR-071 D3) — свои имена, порядок drag'ом, «без этапов».
 *
 * Этап — пользовательская группировка строк, НЕ block_kind: агроном называет
 * этапы сам («Сев и прикатывание»), двигает их порядок и может жить вовсе без
 * них. Удаление этапа не трогает работы — строки остаются «без этапа»
 * (ON DELETE SET NULL по столбцу).
 *
 * Drag реализован нативным HTML5 DnD + кнопки «выше/ниже» дублем: hover-only
 * взаимодействий не делаем (тач), и это же правило адаптивной вёрстки проекта.
 * Порядок уходит на бэк ПОЛНЫМ списком одной транзакцией.
 */
import { useState } from 'react';
import { ArrowDown, ArrowUp, Check, GripVertical, Pencil, Plus, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/lib/toast';
import type { TechCardStage } from '@/lib/planning/techCards';

export interface StagesPanelActions {
  onAdd: (name: string) => void;
  onRename: (stageId: string, name: string) => void;
  onDelete: (stageId: string) => void;
  onReorder: (ids: string[]) => void;
}

export function StagesPanel({
  stages,
  canWrite,
  busy,
  actions,
}: {
  stages: TechCardStage[];
  canWrite: boolean;
  busy: boolean;
  actions: StagesPanelActions;
}) {
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);

  const ordered = [...stages].sort((a, b) => a.seq - b.seq || a.name.localeCompare(b.name, 'ru'));

  const move = (stageId: string, delta: -1 | 1) => {
    const ids = ordered.map((s) => s.id);
    const index = ids.indexOf(stageId);
    const target = index + delta;
    if (index < 0 || target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target], ids[index]];
    actions.onReorder(ids);
  };

  const dropOn = (targetId: string) => {
    if (!dragId || dragId === targetId) return;
    const ids = ordered.map((s) => s.id).filter((id) => id !== dragId);
    ids.splice(ids.indexOf(targetId) + (ordered.findIndex((s) => s.id === dragId) < ordered.findIndex((s) => s.id === targetId) ? 1 : 0), 0, dragId);
    actions.onReorder(ids);
    setDragId(null);
  };

  const submitAdd = () => {
    const name = newName.trim();
    if (!name) {
      toast.error('Назовите этап — пустое имя не сохраняем.');
      return;
    }
    actions.onAdd(name);
    setNewName('');
    setAdding(false);
  };

  const submitRename = () => {
    if (!editing) return;
    const name = editing.name.trim();
    if (!name) {
      toast.error('Назовите этап — пустое имя не сохраняем.');
      return;
    }
    actions.onRename(editing.id, name);
    setEditing(null);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="text-sm font-semibold text-foreground">Этапы карты</h4>
        <span className="text-xs text-muted-foreground">
          {ordered.length === 0
            ? 'карта без этапов — работы идут одним списком'
            : 'свои названия и свой порядок; работы группируются по этапам'}
        </span>
        {canWrite && !adding && (
          <Button size="sm" variant="ghost" className="ml-auto" onClick={() => setAdding(true)}>
            <Plus className="mr-1 h-4 w-4" />
            Этап
          </Button>
        )}
      </div>

      {adding && (
        <form
          className="mt-2 flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            submitAdd();
          }}
        >
          <Input
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Название этапа — например «Сев и прикатывание»"
            className="h-8 max-w-md"
            maxLength={120}
          />
          <Button type="submit" size="sm" disabled={busy}>
            Добавить
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => {
              setAdding(false);
              setNewName('');
            }}
          >
            Отмена
          </Button>
        </form>
      )}

      {ordered.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-2">
          {ordered.map((stage, index) => (
            <li
              key={stage.id}
              draggable={canWrite && !editing}
              onDragStart={() => setDragId(stage.id)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => dropOn(stage.id)}
              onDragEnd={() => setDragId(null)}
              className={
                dragId === stage.id
                  ? 'flex items-center gap-1 rounded-full border border-primary bg-primary/10 px-2 py-1 text-sm opacity-60'
                  : 'flex items-center gap-1 rounded-full border border-border bg-background px-2 py-1 text-sm'
              }
            >
              {canWrite && <GripVertical className="h-3.5 w-3.5 cursor-grab text-muted-foreground" />}
              {editing?.id === stage.id ? (
                <form
                  className="flex items-center gap-1"
                  onSubmit={(e) => {
                    e.preventDefault();
                    submitRename();
                  }}
                >
                  <Input
                    autoFocus
                    value={editing.name}
                    onChange={(e) => setEditing({ id: stage.id, name: e.target.value })}
                    className="h-7 w-48"
                    maxLength={120}
                    aria-label={`Новое имя этапа ${stage.name}`}
                  />
                  <Button type="submit" size="sm" variant="ghost" aria-label="Сохранить имя этапа" disabled={busy}>
                    <Check className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    aria-label="Не переименовывать"
                    onClick={() => setEditing(null)}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </form>
              ) : (
                <>
                  <span className="text-foreground">{stage.name}</span>
                  {canWrite && (
                    <span className="flex items-center">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0"
                        aria-label={`Этап ${stage.name} выше`}
                        disabled={busy || index === 0}
                        onClick={() => move(stage.id, -1)}
                      >
                        <ArrowUp className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0"
                        aria-label={`Этап ${stage.name} ниже`}
                        disabled={busy || index === ordered.length - 1}
                        onClick={() => move(stage.id, 1)}
                      >
                        <ArrowDown className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0"
                        aria-label={`Переименовать этап ${stage.name}`}
                        disabled={busy}
                        onClick={() => setEditing({ id: stage.id, name: stage.name })}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0"
                        aria-label={`Убрать этап ${stage.name}`}
                        disabled={busy}
                        onClick={() => actions.onDelete(stage.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </span>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      {canWrite && ordered.length > 0 && (
        <p className="mt-1 text-xs text-muted-foreground">
          Удаление этапа не трогает работы — они останутся «без этапа».
        </p>
      )}
    </div>
  );
}
